import { makeStyles } from '@material-ui/core';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BilanIcon,
  CaracterisationIcon,
  ClosureIcon,
  ExpeditionIcon,
  HouseDetailIcon,
  ProductionIcon,
  ReceptionIcon,
  ReferentielIcon,
  RessourceIcon,
  StockIcon,
} from 'components/Icons/Menu';
import { AppContext } from 'context';
import { UserRole } from 'types';
import { EtemMenuItem } from '.';
import Menu from './Menu';
import { STATE_MENU_STYLES } from './Menu.style';
import { Routes } from '../../routes';
import { autoriserAcces } from '../../utils/user';

const useStyles = makeStyles({
  ...STATE_MENU_STYLES,
});

const MenuList = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const context = useContext(AppContext);

  const roles = context.getRoles();

  const menuItems = useMemo<Array<Array<EtemMenuItem>>>(() => {
    const shouldBeUser = autoriserAcces(roles, UserRole.ROLE_USER);
    return [
      [
        {
          text: t('menuItem.home'),
          to: '/',
          urlMatch: ['/'],
          icon: <HouseDetailIcon className={classes.icon} />,
          exact: true,
          canAccess: shouldBeUser,
        },
        {
          text: t('menuItem.receptions.mainTitle'),
          to: 'submenu',
          icon: <ReceptionIcon className={classes.icon} />,
          exact: true,
          urlMatch: [Routes.Receptions, Routes.EstimatedReceptions],
          canAccess: shouldBeUser,
          submenu: [
            {
              text: t('menuItem.receptions.receptions'),
              to: Routes.Receptions,
              exact: true,
              canAccess: shouldBeUser,
              urlMatch: [Routes.Receptions, Routes.ReceptionCreate, Routes.ReceptionEdit, Routes.ReceptionShow],
            },
            {
              text: t('menuItem.receptions.estimatedReceptions'),
              to: Routes.EstimatedReceptions,
              exact: true,
              canAccess: shouldBeUser,
              urlMatch: [Routes.EstimatedReceptions, Routes.EstimatedReceptionsEdit],
            },
          ],
        },
        {
          text: t('menuItem.characterizations'),
          to: Routes.Characterizations,
          icon: <CaracterisationIcon className={classes.icon} />,
          exact: false,
          urlMatch: [
            Routes.Characterizations,
            Routes.CharacterizationCreate,
            Routes.CharacterizationEdit,
            Routes.CharacterizationShow,
          ],
          canAccess: shouldBeUser,
        },
        {
          text: t('menuItem.productions'),
          to: Routes.Productions,
          icon: <ProductionIcon className={classes.icon} />,
          exact: false,
          urlMatch: [Routes.Productions, Routes.ProductionCreate, Routes.ProductionEdit, Routes.ProductionShow],
          canAccess: shouldBeUser,
        },
        {
          text: t('menuItem.expeditions'),
          to: Routes.Expeditions,
          icon: <ExpeditionIcon className={classes.icon} />,
          exact: false,
          urlMatch: [Routes.Expeditions, Routes.ExpeditionCreate, Routes.ExpeditionEdit, Routes.ExpeditionShow],
          canAccess: shouldBeUser,
        },
        {
          text: t('menuItem.stocks.mainTitle'),
          to: 'submenu',
          icon: <StockIcon className={classes.icon} />,
          exact: true,
          urlMatch: [Routes.InboundStocks, Routes.ConditionedStocks, Routes.ConditionedStocksByClient],
          canAccess: shouldBeUser,
          submenu: [
            {
              text: t('menuItem.stocks.inbound'),
              to: Routes.InboundStocks,
              exact: true,
              canAccess: shouldBeUser,
              urlMatch: [Routes.InboundStocks],
            },
            {
              text: t('menuItem.stocks.conditioned'),
              to: 'submenu-conditionned',
              exact: true,
              canAccess: shouldBeUser,
              urlMatch: [
                Routes.ConditionedStocks,
                Routes.ConditionedStocksByClient,
                Routes.StockConditionneParRepreneurPage,
              ],
              dropdown: [
                {
                  text: t('menuItem.stocks.global'),
                  to: Routes.ConditionedStocks,
                  exact: true,
                  canAccess: shouldBeUser,
                  urlMatch: [Routes.ConditionedStocks],
                },
                {
                  text: t('menuItem.stocks.conditionedByClient'),
                  to: Routes.ConditionedStocksByClient,
                  exact: true,
                  canAccess: shouldBeUser,
                  urlMatch: [Routes.ConditionedStocksByClient],
                },
                {
                  text: t('menuItem.stocks.conditionneParRepreneur'),
                  to: Routes.StockConditionneParRepreneurPage,
                  exact: true,
                  canAccess: shouldBeUser,
                  urlMatch: [Routes.StockConditionneParRepreneurPage],
                },
              ],
            },
          ],
        },
        {
          text: t('menuItem.closures'),
          to: Routes.Closures,
          icon: <ClosureIcon className={classes.icon} />,
          exact: true,
          urlMatch: [Routes.Closures, Routes.ClosureSuccess, Routes.ClosureClose],
          canAccess: shouldBeUser,
        },
        {
          text: t('menuItem.reports.mainTitle'),
          to: 'submenu',
          icon: <BilanIcon className={classes.icon} />,
          exact: true,
          urlMatch: [Routes.ClientReports, Routes.GlobalReports],
          canAccess: shouldBeUser,
          submenu: [
            {
              text: t('menuItem.reports.client'),
              to: Routes.ClientReports,
              exact: true,
              canAccess: shouldBeUser,
              urlMatch: [Routes.ClientReports],
            },
            {
              text: t('menuItem.reports.global'),
              to: Routes.GlobalReports,
              exact: true,
              canAccess: shouldBeUser,
              urlMatch: [Routes.GlobalReports],
            },
          ],
        },
        {
          text: t('menuItem.referentiels.mainTitle'),
          to: 'submenu',
          icon: <ReferentielIcon className={classes.icon} />,
          exact: true,
          urlMatch: [
            Routes.Clients,
            Routes.ClientContracts,
            Routes.ClientCreate,
            Routes.ClientShow,
            Routes.SortingCenterContractUpdate,
            Routes.SortingCenterContractCreate,
            Routes.SortingCenter,
            Routes.BuyerShow,
            Routes.Buyers,
            Routes.BuyersCreate,
            Routes.SortingCenterContractUpdate,
            Routes.SortingCenterContractCreate,
          ],
          canAccess: shouldBeUser,
          submenu: [
            {
              text: t('menuItem.referentiels.mySortingCenter'),
              to: Routes.SortingCenter,
              exact: true,
              canAccess: shouldBeUser,
              urlMatch: [Routes.SortingCenter],
            },
            {
              text: t('menuItem.referentiels.clients'),
              to: Routes.Clients,
              exact: true,
              canAccess: shouldBeUser,
              urlMatch: [
                Routes.Clients,
                Routes.ClientContracts,
                Routes.ClientCreate,
                Routes.ClientShow,
                Routes.SortingCenterContractUpdate,
                Routes.SortingCenterContractCreate,
              ],
            },
            {
              text: t('menuItem.referentiels.buyers'),
              to: Routes.Buyers,
              exact: true,
              canAccess: shouldBeUser,
              urlMatch: [Routes.BuyerShow, Routes.Buyers, Routes.BuyersCreate],
            },
          ],
        },
        {
          text: t('menuItem.resources'),
          to: Routes.Documents,
          icon: <RessourceIcon className={classes.icon} />,
          exact: false,
          urlMatch: [Routes.Documents],
          canAccess: shouldBeUser,
        },
      ],
    ];
  }, [roles, classes.icon, t]);

  return <Menu menuItems={menuItems} />;
};

export default MenuList;
